import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Stack, Typography } from '@mui/material'

import { GRAY_900 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { PreferredPaymentMethodEnum } from 'constants/user'
import { PreferredPaymentMethodType } from 'models/user/preferredPaymentMethod'
import { useAvailablePaymentMethods } from '../_main/contexts'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUpdatePreferredPaymentMethod } from 'dataQueries'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @component Displays editable select prefilled with saved preferred payment method. Method can be changed and saved with included button.
 * @example
 *  <PreferredPaymentMethodRedesign />
 */
export const PreferredPaymentMethodRedesign: FC = () => {

  const { t } = useTranslation(['preferred_payment_method', 'product_kind', 'request_status'])

  const { clientData } = useUserData()
  const { availablePaymentMethods } = useAvailablePaymentMethods()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const updateMethod = useUpdatePreferredPaymentMethod()

  // Append artificial NONE payment method
  const userAvailablePaymentMethods = useMemo(() => [...availablePaymentMethods, PreferredPaymentMethodEnum.NONE], [availablePaymentMethods])

  const userPreferredMethod = useMemo(() => clientData?.preferredPaymentMethod || PreferredPaymentMethodEnum.NONE, [clientData])

  const [isPrefilled, setIsPrefilled] = useState<boolean>(false)
  const [preferredMethod, setPreferredMethod] = useState<PreferredPaymentMethodType>(PreferredPaymentMethodEnum.NONE)

  const handleValueChange = useCallback((method: PreferredPaymentMethodType) => {
    if (updateMethod.isSuccess || updateMethod.isError) updateMethod.reset()
    setPreferredMethod(method)
  }, [updateMethod])

  // Prefill select with saved method
  useLayoutEffect(() => {
    if (!isPrefilled && userPreferredMethod) {
      setPreferredMethod(userPreferredMethod)
      setIsPrefilled(true)
    }
  }, [isPrefilled, userPreferredMethod])

  // Reset mutation state on destroy
  useEffect(() => {
    return () => {
      updateMethod.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (updateMethod.isSuccess) {
      spawnSuccessToast(t('success'))
    }

    if (updateMethod.isError) {
      spawnErrorToast(t('error'))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMethod.isSuccess, updateMethod.isError])

  return (
    <Stack width="100%" gap={1.2}>

      <MUIDropdown
        button={(isOpen, action) => (
          <MUIDropdownInput
            readOnly
            label={t('label')}
            value={t(`product_kind:${preferredMethod}`)}
            onClick={action}
            sx={{ height: '4rem' }}
          />
        )}
      >
        {userAvailablePaymentMethods.map((method) => (
          <MUIDropdownItem
            key={method}
            onClick={() => handleValueChange(method)}>
            <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
              {t(`product_kind:${method}`)}
            </Typography>
          </MUIDropdownItem>
        ))}
      </MUIDropdown>

      {userPreferredMethod !== preferredMethod &&
        <MUIButton
          type="secondaryBorder"
          isLoading={updateMethod.isPending}
          onClick={() => updateMethod.mutate({ paymentMethod: preferredMethod })}
          sx={{ width: 'fit-content' }}
        >
          {t('save')}
        </MUIButton>
      }

    </Stack>
  )
}
